/**
 * API Service - HTTP client for ShortURL API
 *
 * This module provides a centralized HTTP client for communicating with
 * the ShortURL backend API. It includes error handling, request/response
 * interceptors, and robust error management.
 *
 * @author ShortURL Team
 * @version 1.0.0
 */

import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import { ShortenedUrl, ShortenOptions, UrlStats } from '../lib/url-shortener'
import type {
  AnalyticsLinkListItem,
  AnalyticsOverview,
  CsvDataset,
  LinkAnalytics,
  LinkAnalyticsQuery,
} from '../types/analytics'
import type { DashboardStats } from '../types/dashboard'
import i18n, { resolveLanguage } from '../i18n/config'
import { clearStoredSession } from './sessionStorageKeys'
import { REFRESH_PATH, shouldAttemptRefresh } from './authRetry'
import { requestLanguageHeader } from '../i18n/requestLanguage'

/**
 * 로그인 사용자의 저장된 언어를 화면에 반영한다.
 * 다른 기기에서 바꾼 언어 설정이 로그인 시 따라오도록 하기 위함.
 */
function applyUserLanguage(user: unknown): void {
  const preference = (user as { preferences?: { language?: string } })
    ?.preferences?.language
  if (!preference) {
    return
  }

  const target = resolveLanguage(preference)
  if (i18n.language !== target) {
    void i18n.changeLanguage(target)
  }
}

/**
 * Base API response structure
 */
interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
  meta?: {
    page?: number
    limit?: number
    total?: number
    totalPages?: number
  }
}

/**
 * Authentication response
 */
interface AuthResponse {
  user: {
    _id: string
    id: string
    email: string
    username: string
    name: string
    role: 'user' | 'admin'
    plan: string
    isActive: boolean
    createdAt: string
  }
  token: string
  refreshToken?: string
  expiresIn?: number
}

/**
 * User registration data
 */
interface RegisterData {
  name: string
  email: string
  password: string
}

/**
 * User login data
 */
interface LoginData {
  email: string
  password: string
  /** 로그인 유지. 켜면 브라우저를 닫아도 세션이 남는다. */
  rememberMe?: boolean
}

/**
 * Custom error class for API errors
 */
export class ApiServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public details?: any
  ) {
    super(message)
    this.name = 'ApiServiceError'
  }
}

/** 분석 API 공통 쿼리 파라미터 (기본 타임존은 브라우저 설정을 따른다) */
function buildAnalyticsParams(
  query: LinkAnalyticsQuery
): Record<string, string> {
  const params: Record<string, string> = {
    range: query.range ?? '7d',
    tz:
      query.tz ??
      Intl.DateTimeFormat().resolvedOptions().timeZone ??
      'Asia/Seoul',
  }
  if (query.includeBots) {
    params.includeBots = 'true'
  }
  return params
}

/**
 * Main API service class
 */
/** 갱신 후 한 번만 다시 보내기 위한 표시 */
interface RetriableRequest extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean
}

class ApiService {
  public client: AxiosInstance
  private authToken: string | null = null
  private pendingRefresh: Promise<unknown> | null = null

  constructor() {
    // Determine API URL based on current domain
    let baseURL: string

    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname

      if (hostname === 'app.shorty.kr') {
        // App subdomain - use main API server
        baseURL = 'https://shorty.kr/api'
      } else if (hostname === 'admin.shorty.kr') {
        // Admin subdomain - use main API server
        baseURL = 'https://shorty.kr/api'
      } else if (hostname === 'shorty.kr') {
        // Production environment
        baseURL = 'https://shorty.kr/api'
      } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Development environment
        baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5199/api'
      } else {
        // Fallback
        baseURL = 'https://shorty.kr/api'
      }
    } else {
      // Server-side rendering or testing
      baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5199/api'
    }

    this.client = axios.create({
      baseURL,
      timeout: 10000,
      // 재발급 토큰이 httpOnly 쿠키로 오간다. 켜 두지 않으면
      // app.shorty.kr → shorty.kr 요청에 쿠키가 실리지 않는다.
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
    this.loadTokenFromStorage()
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      config => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`
        }
        // 화면에서 고른 언어를 알린다. 안 보내면 브라우저 언어가 응답
        // 언어를 정해, 영어 화면에 한국어 문구가 섞여 나온다.
        config.headers['Accept-Language'] = requestLanguageHeader(
          i18n.resolvedLanguage ?? i18n.language
        )
        return config
      },
      error => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const request = error.config as RetriableRequest | undefined

        // 접근 토큰이 만료됐을 뿐이면 조용히 갱신하고 한 번 다시 보낸다
        if (
          request &&
          shouldAttemptRefresh({
            status: error.response?.status,
            url: request.url,
            alreadyRetried: Boolean(request._retriedAfterRefresh),
          })
        ) {
          try {
            await this.refreshOnce()
            request._retriedAfterRefresh = true
            return await this.client.request(request)
          } catch {
            // 갱신까지 실패하면 진짜로 세션이 끝난 것이다
            this.endSession()
            throw this.transformError(error)
          }
        }

        if (
          error.response?.status === 401 &&
          !request?.url?.includes(REFRESH_PATH)
        ) {
          this.endSession()
        }

        const apiError = this.transformError(error)
        throw apiError
      }
    )
  }

  /**
   * 갱신은 한 번만 날린다.
   * 토큰이 만료되면 보통 여러 요청이 동시에 401 을 받는데,
   * 각자 갱신을 걸면 서버에 같은 요청이 몰리고 쿠키 회전이 엇갈린다.
   */
  private refreshOnce(): Promise<unknown> {
    if (!this.pendingRefresh) {
      this.pendingRefresh = this.refreshToken().finally(() => {
        this.pendingRefresh = null
      })
    }
    return this.pendingRefresh
  }

  /**
   * 접근 토큰이 없을 때 재발급 쿠키만으로 세션을 되살려 본다.
   * 브라우저를 닫았다 열거나 저장소가 비워져도 로그인이 유지된다.
   */
  async restoreSession(): Promise<boolean> {
    try {
      await this.refreshOnce()
      return true
    } catch {
      return false
    }
  }

  /** 저장된 자격 증명을 지우고 로그인 화면으로 보낸다 */
  private endSession(): void {
    console.warn('Session ended, redirecting to login...')
    this.authToken = null

    // 관리자 화면은 admin_token 을 쓴다. 예전에는 여기서 token·user 만
    // 지워서 로그인 화면으로 보내 놓고도 관리자 토큰은 남아 있었다.
    const hostname =
      typeof window !== 'undefined' ? window.location.hostname : null
    clearStoredSession(hostname)

    // 세 앱 모두 로그인 경로가 /login 이라 분기가 필요 없다
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== '/login'
    ) {
      window.location.href = '/login'
    }
  }

  private loadTokenFromStorage(): void {
    try {
      // For admin app, prioritize admin_token
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname === 'admin.shorty.kr') {
          this.authToken =
            localStorage.getItem('admin_token') || localStorage.getItem('token')
        } else {
          this.authToken = localStorage.getItem('token')
        }
      } else {
        this.authToken = localStorage.getItem('token')
      }
    } catch (error) {
      console.warn('Failed to load token from storage:', error)
    }
  }

  private storeToken(token: string): void {
    try {
      this.authToken = token
      // For admin app, also store as admin_token for consistency
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname
        if (hostname === 'admin.shorty.kr') {
          localStorage.setItem('admin_token', token)
        }
      }
      localStorage.setItem('token', token)
    } catch (error) {
      console.warn('Failed to store token:', error)
    }
  }

  private transformError(error: AxiosError): ApiServiceError {
    if (error.response) {
      const responseData = error.response.data as any
      const errorCode = responseData?.code || 'API_ERROR'

      // express-validator 응답은 개별 필드 오류를 errors 배열로 준다.
      // 이를 버리면 사용자에게 'Validation failed' 만 보이므로 메시지에 합친다.
      const validationErrors: Array<{ msg?: string; message?: string }> =
        Array.isArray(responseData?.errors) ? responseData.errors : []
      const validationMessage = validationErrors
        .map(item => item?.msg || item?.message)
        .filter(Boolean)
        .join(', ')

      const errorMessage =
        validationMessage ||
        responseData?.error ||
        responseData?.message ||
        error.message

      return new ApiServiceError(
        errorMessage,
        errorCode,
        error.response.status,
        responseData?.details ||
          responseData?.data ||
          (validationErrors.length > 0
            ? { errors: validationErrors }
            : undefined)
      )
    } else if (error.request) {
      return new ApiServiceError(
        'Network error - please check your connection',
        'NETWORK_ERROR',
        0
      )
    } else {
      return new ApiServiceError(error.message, 'REQUEST_ERROR')
    }
  }

  // Public API methods

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await this.client.post<ApiResponse<AuthResponse>>(
      '/auth/register',
      data
    )
    if (response.data.success && response.data.data) {
      this.storeToken(response.data.data.token)
    }
    return response.data.data!
  }

  async login(data: LoginData): Promise<AuthResponse> {
    // Errors propagate so the login page can handle email verification.
    const response = await this.client.post<ApiResponse<AuthResponse>>(
      '/auth/login',
      data
    )
    if (response.data.success && response.data.data) {
      this.storeToken(response.data.data.token)
      applyUserLanguage(response.data.data.user)
    }
    return response.data.data!
  }

  async logout(): Promise<void> {
    try {
      // 서버가 재발급 쿠키를 지운다. 쿠키는 httpOnly 라 여기서 못 지운다.
      await this.client.post('/auth/logout')
    } finally {
      this.authToken = null
      // 관리자 화면은 admin_token 도 쓴다. 예전에는 token·user 만 지워
      // 관리자 토큰이 남았다.
      const hostname =
        typeof window !== 'undefined' ? window.location.hostname : null
      clearStoredSession(hostname)
    }
  }

  async createShortUrl(
    originalUrl: string,
    options: ShortenOptions = {}
  ): Promise<ShortenedUrl> {
    const response = await this.client.post<ApiResponse<ShortenedUrl>>(
      '/urls',
      {
        originalUrl,
        ...options,
      }
    )
    return response.data.data!
  }

  async getShortUrl(shortCode: string): Promise<ShortenedUrl> {
    const response = await this.client.get<ApiResponse<ShortenedUrl>>(
      `/urls/${shortCode}`
    )
    return response.data.data!
  }

  /**
   * 사용자 링크 목록.
   *
   * 실패를 목 데이터로 감추지 않는다. 예전 구현은 요청이 실패하면 예시 링크
   * 5건을 반환해서, 장애 상황에서 사용자에게 존재하지 않는 링크가 실제
   * 데이터처럼 보였다. 오류는 호출부가 처리하도록 그대로 전파한다.
   */
  async getUserUrls(options: Record<string, unknown> = {}): Promise<{
    urls: ShortenedUrl[]
    total: number
    page: number
    limit: number
  }> {
    const response = await this.client.get<
      ApiResponse<{
        urls: ShortenedUrl[]
        total: number
        page: number
        limit: number
      }>
    >('/urls', { params: options })

    const data = response.data.data
    return {
      urls: data?.urls ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? 1,
      limit: data?.limit ?? (options.limit as number) ?? 10,
    }
  }

  async deleteShortUrl(shortCode: string): Promise<void> {
    await this.client.delete(`/urls/${shortCode}`)
  }

  async getUrlAnalytics(shortCode: string): Promise<UrlStats> {
    const response = await this.client.get<ApiResponse<UrlStats>>(
      `/urls/${shortCode}/analytics`
    )
    return response.data.data!
  }

  /** 대시보드 통계 (증감률 포함) */
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.client.get<ApiResponse<DashboardStats>>(
      '/analytics/dashboard'
    )
    return response.data.data!
  }

  /** 계정 전체 분석 개요 (기간·타임존·봇 포함 여부 반영) */
  async getAnalyticsOverview(
    query: LinkAnalyticsQuery = {}
  ): Promise<AnalyticsOverview> {
    const response = await this.client.get<ApiResponse<AnalyticsOverview>>(
      '/analytics/overview',
      { params: buildAnalyticsParams(query) }
    )
    return response.data.data!
  }

  async checkShortCodeAvailability(
    shortCode: string
  ): Promise<{ available: boolean }> {
    const response = await this.client.get<ApiResponse<{ available: boolean }>>(
      `/urls/check/${shortCode}`
    )
    return response.data.data!
  }

  async getUserProfile(): Promise<AuthResponse['user']> {
    const response =
      await this.client.get<ApiResponse<{ user: AuthResponse['user'] }>>(
        '/auth/me'
      )
    return response.data.data!.user
  }

  async updateUserProfile(data: {
    firstName?: string
    lastName?: string
    name?: string
    bio?: string
    website?: string
    location?: string
    preferences?: {
      theme?: 'light' | 'dark' | 'system'
      language?: string
      notifications?: {
        email?: boolean
        push?: boolean
        reports?: boolean
      }
      privacy?: {
        publicProfile?: boolean
        showStats?: boolean
      }
    }
  }): Promise<AuthResponse['user']> {
    const response = await this.client.put<
      ApiResponse<{ user: AuthResponse['user'] }>
    >('/auth/profile', data)
    return response.data.data!.user
  }

  async changePassword(data: {
    currentPassword: string
    newPassword: string
  }): Promise<void> {
    await this.client.put<ApiResponse<void>>('/auth/change-password', data)
  }

  /** 링크별 정밀 분석 조회 */
  async getLinkAnalytics(
    shortCode: string,
    query: LinkAnalyticsQuery = {}
  ): Promise<LinkAnalytics> {
    const response = await this.client.get<ApiResponse<LinkAnalytics>>(
      `/analytics/link/${encodeURIComponent(shortCode)}`,
      { params: buildAnalyticsParams(query) }
    )
    return response.data.data!
  }

  /** 링크 선택기용 목록 (검색어로 필터) */
  async getAnalyticsLinks(search = ''): Promise<AnalyticsLinkListItem[]> {
    const response = await this.client.get<
      ApiResponse<AnalyticsLinkListItem[]>
    >('/analytics/links', { params: search ? { search } : undefined })
    return response.data.data ?? []
  }

  /** 링크 분석 CSV 다운로드 (Blob 으로 받아 브라우저 저장) */
  async downloadLinkAnalyticsCsv(
    shortCode: string,
    dataset: CsvDataset,
    query: LinkAnalyticsQuery = {}
  ): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/analytics/link/${encodeURIComponent(shortCode)}/export`,
      {
        params: { ...buildAnalyticsParams(query), dataset },
        responseType: 'blob',
      }
    )
    return response.data
  }

  async refreshToken(): Promise<{ token: string }> {
    // Errors propagate; the caller decides whether to log out on failure.
    const response = await this.client.post<ApiResponse<{ token: string }>>(
      '/auth/refresh-token'
    )
    if (response.data.success && response.data.data?.token) {
      this.storeToken(response.data.data.token)
      return response.data.data
    }
    throw new Error('Invalid refresh token response')
  }
}

// Create and export singleton instance
export const apiService = new ApiService()

// Export types
export type { ApiResponse, AuthResponse, RegisterData, LoginData }
